import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@reflex/ui/components/ui/command';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { cn } from '@reflex/ui/lib/utils';
import { api } from '@/lib/api';
import type { AgentEnvVar } from '@/lib/types';
import type { ScopedSecret } from '@reflex/shared';
import {
  useAvailableSecrets,
  type AvailableSecretsOwner,
} from '@/hooks/secrets/useAvailableSecrets';
import { useDebouncedValue } from '@reflex/ui/hooks/useDebouncedValue';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { useMyTeams } from '@/hooks/auth/useMyTeams';
import { queryKeys, orgScope } from '@/lib/query-keys';
import { SecretCreateForm, type CreateSecretTier } from '@/components/secrets/SecretCreateForm';
import { ENV_VAR_ROW_FIELD_CLASS } from './field-styles';
import { groupSecretsByCategory } from './group-secrets';

export type { AvailableSecretsOwner };

interface SecretRefPickerProps {
  entry: AgentEnvVar;
  hasStoredSecrets: boolean;
  onChange: (patch: Partial<AgentEnvVar>) => void;
  /**
   * Owner context for the picker. Drives which user/team-tier secrets
   * appear and, in a follow-up, which tiers the inline "Add new secret"
   * flow can write to. When omitted the picker falls back to the
   * currently-authenticated user (launch dialog default).
   */
  owner?: AvailableSecretsOwner;
}

export function SecretRefPicker({
  entry,
  hasStoredSecrets,
  onChange,
  owner,
}: SecretRefPickerProps) {
  const queryClient = useQueryClient();
  const { user, can } = useAuth();
  const { activeOrganization } = useOrganization();
  const myTeams = useMyTeams();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'select' | 'create'>('select');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 150);

  // Default owner: the launching user. The launch dialog never passes
  // `owner` explicitly because that's its only meaningful context.
  // Persona forms always pass an explicit owner derived from the
  // persona's `ResourceOwner`.
  const effectiveOwner: AvailableSecretsOwner | undefined = useMemo(() => {
    if (owner) return owner;
    if (user) return { ownerType: 'user', ownerUserId: user.id };
    return undefined;
  }, [owner, user]);

  const { secrets, isLoading } = useAvailableSecrets(debouncedSearch, effectiveOwner);

  // Build the tier options the inline-create form shows. The list is
  // gated by the picker's owner scope and the caller's permissions so
  // an operator can't accidentally create a secret in a tier they
  // wouldn't be able to read back from the picker.
  const orgId = activeOrganization?.id ?? null;
  const canManageOrgSecrets = can('secrets:manage');
  const tierOptions = useMemo<CreateSecretTier[]>(() => {
    const opts: CreateSecretTier[] = [];
    if (!effectiveOwner) {
      // No resolved owner yet (auth still loading). Don't offer any
      // tier — the form falls back to its no-selector layout.
      return opts;
    }
    if (effectiveOwner.ownerType === 'organization') {
      // Org-owned personas can only bind org-tier secrets.
      if (canManageOrgSecrets) {
        opts.push({ id: 'org', kind: 'org', label: 'Organization' });
      }
      return opts;
    }
    if (effectiveOwner.ownerType === 'user') {
      opts.push({ id: 'user', kind: 'user', label: 'Personal' });
      for (const team of myTeams) {
        opts.push({
          id: `team:${team.ownerTeamId}`,
          kind: 'team',
          label: `Team: ${team.name}`,
          teamId: team.ownerTeamId,
        });
      }
      if (canManageOrgSecrets) {
        opts.push({ id: 'org', kind: 'org', label: 'Organization' });
      }
      return opts;
    }
    // ownerType === 'team' (team-owned persona)
    const teamId = effectiveOwner.ownerTeamId;
    if (teamId) {
      const teamName = myTeams.find((t) => t.ownerTeamId === teamId)?.name ?? 'this team';
      opts.push({
        id: `team:${teamId}`,
        kind: 'team',
        label: `Team: ${teamName}`,
        teamId,
      });
    }
    if (canManageOrgSecrets) {
      opts.push({ id: 'org', kind: 'org', label: 'Organization' });
    }
    return opts;
  }, [effectiveOwner, myTeams, canManageOrgSecrets]);

  // Default tier matches the picker scope: Personal for user scope,
  // the team itself for team scope, Organization for org scope.
  const defaultTierId = useMemo(() => {
    if (tierOptions.length === 0) return undefined;
    if (effectiveOwner?.ownerType === 'team') {
      const match = tierOptions.find((o) => o.kind === 'team');
      if (match) return match.id;
    }
    if (effectiveOwner?.ownerType === 'organization') {
      const match = tierOptions.find((o) => o.kind === 'org');
      if (match) return match.id;
    }
    // user scope (or fallback)
    const personal = tierOptions.find((o) => o.kind === 'user');
    if (personal) return personal.id;
    return tierOptions[0].id;
  }, [tierOptions, effectiveOwner]);

  /**
   * Common post-create work: bind the entry's `secretRef` to the new
   * row, invalidate the matching list cache, and close the popover.
   * Org-tier rows continue to use the friendly `name` as the ref;
   * user/team rows use the freshly-minted `sec_*` id (matches the
   * `/secrets/available` response shape).
   */
  function handleCreateSuccess(
    tier: CreateSecretTier,
    result: ScopedSecret | undefined,
    fallbackName: string,
  ) {
    if (tier.kind === 'org') {
      onChange({ secretRef: fallbackName, value: undefined });
    } else if (result?.id) {
      onChange({ secretRef: result.id, value: undefined });
    } else {
      // The server should always return the new row, but if it doesn't
      // surface a friendly fallback rather than leaving the field empty.
      onChange({ secretRef: fallbackName, value: undefined });
    }
    setMode('select');
    setOpen(false);
    setSearch('');
  }

  const createOrgSecret = useMutation({
    mutationFn: ({ name, value }: { name: string; value: string }) => api.setSecret(name, value),
    onSuccess: async (_result, variables) => {
      // Refresh org-tier slots + the availability list so the new row
      // appears the next time the popover opens.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.secrets(orgScope(orgId)) }),
        queryClient.invalidateQueries({ queryKey: ['secrets'] }),
      ]);
      handleCreateSuccess(
        { id: 'org', kind: 'org', label: 'Organization' },
        undefined,
        variables.name,
      );
    },
    onError: (err: Error) => toast.error('Failed to save secret', { description: err.message }),
  });

  const createUserSecret = useMutation({
    mutationFn: ({ name, value }: { name: string; value: string }) =>
      api.createUserSecret({ name, value }),
    onSuccess: async (result, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.userSecrets(orgScope(orgId)) }),
        queryClient.invalidateQueries({ queryKey: ['secrets'] }),
      ]);
      handleCreateSuccess({ id: 'user', kind: 'user', label: 'Personal' }, result, variables.name);
    },
    onError: (err: Error) => toast.error('Failed to save secret', { description: err.message }),
  });

  const createTeamSecret = useMutation({
    mutationFn: ({ teamId, name, value }: { teamId: string; name: string; value: string }) => {
      if (!orgId) {
        return Promise.reject(
          new Error('Cannot create a team secret without an active organization.'),
        );
      }
      return api.createTeamSecret(orgId, teamId, { name, value });
    },
    onSuccess: async (result, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['teams', variables.teamId, 'secrets'] }),
        queryClient.invalidateQueries({ queryKey: ['secrets'] }),
      ]);
      const team = myTeams.find((t) => t.ownerTeamId === variables.teamId);
      handleCreateSuccess(
        {
          id: `team:${variables.teamId}`,
          kind: 'team',
          label: `Team: ${team?.name ?? variables.teamId}`,
          teamId: variables.teamId,
        },
        result,
        variables.name,
      );
    },
    onError: (err: Error) => toast.error('Failed to save secret', { description: err.message }),
  });

  const isCreating =
    createOrgSecret.isPending || createUserSecret.isPending || createTeamSecret.isPending;

  // Reset transient picker state whenever the popover closes so reopening
  // always starts from a clean "select" view.
  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setMode('select');
      setSearch('');
    }
  }

  const selectedRef = entry.secretRef;
  // The picker stores `sec_*` ids for user/team rows and friendly names
  // for org rows, so match against both shapes when surfacing the label.
  const selectedSecret = selectedRef
    ? secrets.find((s) => s.id === selectedRef || s.name === selectedRef)
    : undefined;

  let label: string;
  if (selectedSecret) {
    label = selectedSecret.label;
  } else if (selectedRef && hasStoredSecrets) {
    label = selectedRef;
  } else if (selectedRef) {
    label = selectedRef;
  } else {
    label = 'Select a secret…';
  }

  const grouped = groupSecretsByCategory(secrets);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Secret"
          className={cn(
            // Shared row-field sizing (see `ENV_VAR_ROW_FIELD_CLASS`) + button chrome.
            ENV_VAR_ROW_FIELD_CLASS,
            'justify-between gap-1 border-input bg-transparent px-2 font-normal',
            'hover:bg-transparent hover:text-foreground',
            !selectedRef && 'text-muted-foreground hover:text-muted-foreground',
          )}
        >
          <span className="min-w-0 truncate">{label}</span>
          <ChevronsUpDown className="size-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto min-w-[14rem] max-w-sm p-0"
        align="start"
        side="bottom"
        sideOffset={4}
        collisionPadding={16}
        data-testid="secret-ref-picker-content"
      >
        {mode === 'select' ? (
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search secrets…"
              className="h-8 text-xs"
              value={search}
              onValueChange={setSearch}
            />
            {/* Wheel scrolling inside a Dialog's scroll lock is handled
                centrally by the shared `CommandList` primitive. */}
            <CommandList className="overscroll-contain" style={{ height: '18rem' }}>
              {isLoading && secrets.length === 0 ? (
                <div className="px-3 py-4 text-xs text-muted-foreground">Loading…</div>
              ) : secrets.length === 0 ? (
                <div className="px-3 py-4 text-xs text-muted-foreground">
                  {debouncedSearch.trim()
                    ? `No secrets match “${debouncedSearch.trim()}”.`
                    : 'No secrets available yet.'}
                </div>
              ) : (
                grouped.map((group) => (
                  <CommandGroup key={group.label} heading={group.label}>
                    {group.secrets.map((secret) => {
                      // User/team rows store the opaque `sec_*` id as
                      // the env-var `secretRef`; org rows use the
                      // friendly name. Match the resolver path on the
                      // server side in `agent-env-vars.ts`.
                      const refValue = secret.id ?? secret.name;
                      const isSelected = selectedRef === refValue || selectedRef === secret.name;
                      return (
                        <CommandItem
                          key={refValue}
                          value={refValue}
                          onSelect={() => {
                            onChange({ secretRef: refValue, value: undefined });
                            setOpen(false);
                            setSearch('');
                          }}
                          className="text-xs"
                        >
                          <Check
                            className={cn(
                              'mr-1.5 h-3 w-3 shrink-0',
                              isSelected ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          <span className="min-w-0 flex-1 truncate font-mono" title={secret.label}>
                            {secret.label}
                          </span>
                          {(secret.category === 'user' || secret.category === 'team') && (
                            <Badge
                              variant="outline"
                              className="ml-1.5 shrink-0 px-1 py-0 text-[9px] font-normal"
                            >
                              {secret.category === 'user' ? 'Personal' : 'Team'}
                            </Badge>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))
              )}
            </CommandList>
            {secrets.length > 0 && <CommandSeparator />}
            <div className="p-1">
              <Button
                variant="ghost"
                onClick={() => setMode('create')}
                className="h-auto w-full justify-start gap-0 rounded-sm px-2 py-1.5 text-xs font-normal text-muted-foreground"
                data-testid="secret-ref-picker-add-new"
              >
                <Plus className="mr-1.5 size-3 shrink-0" />
                {debouncedSearch.trim() ? (
                  <>
                    Add new secret
                    <span className="ml-1 font-mono text-foreground">
                      “{debouncedSearch.trim()}”
                    </span>
                  </>
                ) : (
                  <>Add new secret</>
                )}
              </Button>
            </div>
          </Command>
        ) : (
          <div className="p-3" data-testid="secret-ref-picker-create-form">
            <p className="mb-3 text-xs font-medium">Add custom secret</p>
            <SecretCreateForm
              // Prefill from the raw `search` (synchronous), not
              // `debouncedSearch` (150ms lag). `SecretCreateForm` captures
              // `initialName` exactly once in `useState`, so if we hand it
              // the still-stale debounced value the user's typed text is
              // permanently lost the instant they click "Add new secret".
              initialName={search.trim()}
              isPending={isCreating}
              tierOptions={tierOptions.length > 0 ? tierOptions : undefined}
              defaultTierId={defaultTierId}
              onSubmit={(name, value, tier) => {
                if (!tier) {
                  // No tier picker rendered (no options available). Fall
                  // back to org-tier for backwards compatibility — this
                  // branch is only reachable when the form was rendered
                  // with `tierOptions={undefined}` because the owner
                  // scope hadn't resolved yet, which the disabled-state
                  // guard above should prevent.
                  createOrgSecret.mutate({ name, value });
                  return;
                }
                if (tier.kind === 'user') {
                  createUserSecret.mutate({ name, value });
                } else if (tier.kind === 'team' && tier.teamId) {
                  createTeamSecret.mutate({ teamId: tier.teamId, name, value });
                } else {
                  createOrgSecret.mutate({ name, value });
                }
              }}
              onCancel={() => setMode('select')}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
